import React, { useMemo } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import AudioPreview from '@components/shared/AudioPreview'
import useUpdateQueryParams from '@hooks/useUpdateQueryParams'
import Title from '@models/Title'
import { useSession } from '@stores/globalData/globalData.store'
import { buildFormatString } from '@utils/format'
import PageRoutes from '@utils/page-routes'

import type { TitleType } from '@models/Title'

interface Props {
  title: TitleType
  showPriceColumn: boolean
  paginationNumLink?: number | null
}

const ResultRow: React.FC<Props> = ({
  title: titleProp,
  showPriceColumn,
  paginationNumLink,
}: Props) => {
  const { t } = useTranslation()
  const [search] = useSearchParams()
  const session = useSession()
  const title = useMemo(() => new Title(titleProp), [titleProp])

  const genre = title.metadata.genres.map((genre) => t(`genre.${genre}`)).join(', ')

  return (
    <Link
      to={PageRoutes.musicTitlePath(title.uuid, title.product?.slug)}
      state={{ query: search.toString() }}
      className={classNames(
        'flex flex-row bg-white dark:bg-black-bg2 rounded mb-0.5 px-2.5 text-black dark:text-gray group hover:bg-opacity-50 dark:hover:bg-opacity-75',
        {
          'item-page': typeof paginationNumLink === 'number',
        }
      )}
      data-page={paginationNumLink}>
      <div className="flex flex-row w-[30px]">
        <AudioPreview
          id={title.uuid}
          url={title.metadata.audio_preview_path}
          className="text-orange hover:text-orange-dark text-[25px] disabled:text-gray-dim"
        />
      </div>
      <p className="flex-1 px-1.5 py-3 font-normal basis-1/4 ellipsify">{title.title}</p>
      <p className="font-normal py-3 px-1.5 flex-1 basis-[12%] ellipsify">
        {title.metadata.primary_contributor ?? '--'}
      </p>
      <p className="font-normal py-3 px-1.5 flex-1 basis-[5%] ellipsify">
        {buildFormatString(title.metadata) || '--'}
      </p>
      {genre ? (
        <FilterLink name="genre" value={genre} />
      ) : (
        <p className="font-normal py-3 px-1.5 flex-1 basis-[5%] ellipsify">--</p>
      )}
      {showPriceColumn && (
        <p className="font-normal py-3 px-1.5 flex-1 basis-[3%]">
          {title.shouldShowPrice(session) ? title.formattedPrice() : ''}
        </p>
      )}
    </Link>
  )
}
export default React.memo(ResultRow)

/**
 * Link used to filter based on properties in the result row
 */
interface FilterLinkProps {
  name: string
  value?: string
}
const FilterLink: React.FC<FilterLinkProps> = ({ name, value }: FilterLinkProps) => {
  const { addParam, params } = useUpdateQueryParams({ pathname: PageRoutes.musicSearchPath() })
  return (
    <button
      className={classNames(
        'font-normal py-3 px-1.5 flex-1 basis-[5%] ellipsify text-left',
        params[name]?.includes(value ?? '') ? 'cursor-default' : 'hover:text-orange'
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()

        if (value && !params[name]?.includes(value)) addParam(name, value)
      }}>
      {value ?? '--'}
    </button>
  )
}
